import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";
import "./VhsFrameComponent";
import "./HomeButtonComponent";
import "./TimerComponent";
import "./InventoryComponent";
import { InventoryComponent } from "@web/components/InventoryComponent";
import "./PuzzleComponent";
import { PuzzleComponent } from "@web/components/PuzzleComponent";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host {
        font-family: "F3270 Semi-Condensed";
        width: 100%;
        max-width: var(--game-width);
        height: calc(100% - var(--footer-height));
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto calc(35vh + 10px) minmax(calc(35vh + 10px), 1fr) auto;
        grid-column-gap: 0px;
        grid-row-gap: 0px;
        
        position: relative;

        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
    }
    
    :host:after {
        content: "";
        position: absolute;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.7);
        pointer-events: none;
    }

    .title {
        text-align: center;
        margin-top: 10px;
        overflow: auto;
        
        animation: slideDown var(--room-title-slide-down-duration) ease-out;
        z-index: 15;
    }

    .header {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        margin-top: 10px;
    }

    .header img {
        width: auto;
        height: 100%;
        image-rendering: pixelated;
    }

    .header img:nth-child(n + 2) {
        position: absolute;
    }

    .content {
        flex-grow: 1;
        overflow: auto;
        margin-top: 10px;
        padding: 0 10px;

        z-index: 15;
        pointer-events: none;
    }

    .content p {
        margin: 0 0 10px 0;
        height: min-content;
    }

    .content p:last-of-type {
        margin: 0;
    }

    .footer {
        position: fixed;
        bottom: 0;
        width: 100%;
        max-width: var(--game-width);
        
        background-color: var(--footer-background-color);
        border: 1px solid var(--footer-border-color);
        margin-top: 10px;
        height: var(--footer-height);
        
        z-index: 15;
    }

    .footer .buttons {
        display: flex;
        flex-direction: column;
        overflow: auto;
        padding: 10px 10px 0 10px;
    }

    .footer .button {
        background-color: var(--button-background-color);
        color: var(--button-color);
        padding: 5px 10px;
        margin: 0 0 10px 10px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
        
        transition: opacity var(--button-hover-transition-duration) ease-out;
    }

    .footer .button.active,
    .footer .button:hover {
        opacity: 0.8;
    }

    /* Transitions */
    
    .transition-overlay {
        position: fixed;
        inset: 0;
        background-color: #000000;
        opacity: 0;
        pointer-events: none;
        transition: opacity var(--change-room-fade-in-duration) ease-out;
    }

    .transition-overlay.active {
        opacity: 1;
    }
    
    /* Animations */
    
    @keyframes slideDown {
        from {
            opacity: 0;
            transform: translateY(-20px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();
    /** Is there currently a transition overlay on the screen? */
    private _isTransitioning: boolean = false;
    /** Audio player that is playing soundtrack from gameState.roomSound */
    private _roomAudio?: HTMLAudioElement;
    /** Audio player that is playing action sounds */
    private _actionAudio?: HTMLAudioElement;
    /** Typing speed in ms */
    private _typingSpeed: number = 46;
    /** Inventory */
    private _inventory: GameObjectReference[] = [];

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();
    private _talkingAudio?: HTMLAudioElement;
    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        await this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private async updateGameState(state: GameState): Promise<void> {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        this._inventory = await this._gameRouteService.getInventory();

        // Refresh the web component
        this.render(true);

        // Update inventory
        this.updateInventoryComponent();

        // Start fade in transition
        this.fadeIn();

        // Start playing room soundtrack
        this.playRoomSound();
    }

    /**
     * Gets inventory element and passes inventory data into it
     *
     * @private
     *
     * @author Kyrylo Maliuha
     */
    private updateInventoryComponent(): void {
        const inventory: InventoryComponent | undefined | null = this.shadowRoot?.querySelector("inventory-bar");

        if (inventory) {
            inventory.inventory = this._inventory;
        }
    }

    /**
     * Render the contents of this page
     *
     * @param noTransition If true then fade transition will not start
     */
    private render(noTransition: boolean = false): void {
        if (!this.shadowRoot) {
            return;
        }

        // checks if game state is not undefined and then applies roomBackground as background-image CSS property to :host
        if (this._currentGameState) {
            this.style.backgroundImage = `url(${this._currentGameState.roomBackground})`;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderTitle()}
            ${this.renderHeader()}
            ${this.renderContent()}
            ${this.renderFooter()}
            
            <!-- VHS frame -->
            <vhs-frame></vhs-frame>
            
            <!-- puzzle -->
            <puzzle-container></puzzle-container>
            
            <!-- Transition overlay -->
            <div class="transition-overlay ${!noTransition ? "active" : null}" id="transitionOverlay" />
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        if (roomImages && roomImages.length > 0) {
            return `
                <div class="header">
                    ${roomImages.map(url => `<img src="/assets/img/rooms/${url}.png" />`).join("")}
                </div>
            `;
        }

        return "";
    }

    private startTalkingSfx(soundUrl: string = "/assets/sound/voice_sans.mp3"): void {
        if (!this._talkingAudio) {
            this._talkingAudio = new Audio(soundUrl);
            this._talkingAudio.loop = true;
            this._talkingAudio.volume = 0.4; // optional, lower volume
            this._talkingAudio.play().catch(console.log);
        }
    }

    private stopTalkingSfx(): void {
        if (this._talkingAudio) {
            this._talkingAudio.pause();
            this._talkingAudio.src = "";
            this._talkingAudio = undefined;
        }
    }

    /**
     * Render the content element with typing animation
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): HTMLElement {
        const element: HTMLElement = html`<div class="content"></div>`;
        const text: string = this._currentGameState?.text.join("\n\n") ?? "";
        let i: number = 0;

        // start the talking sound loop
        this.startTalkingSfx("/assets/sound/voice_sans.mp3");

        const tick: () => void = (): void => {
            if (i < text.length) {
                element.textContent = (element.textContent ?? "") + text.charAt(i);
                i++;
                setTimeout(tick, this._typingSpeed);
            }
            else {
            // stop the sound after dialogue ends
                this.stopTalkingSfx();
            }
        };

        tick();

        return element;
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        return html`
            <div class="footer">
                <div class="buttons">
                    <div>
                        ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                    </div>
                    <div>
                        ${this._selectedActionButton
                            ? this._currentGameState?.objects.map(button => this.renderGameObjectButton(button)) || ""
                            : ""
                        }
                    </div>
                </div>

                <inventory-bar></inventory-bar>
                
                <home-button></home-button>
            </div>
        `;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedActionButton === button ? "active" : ""}">
                ${button.name}
            </a>
        `;

        if (button.name === "") {
            element.style.visibility = "hidden";
        }

        element.addEventListener("click", () => void this.handleClickAction(button));
        element.addEventListener("mouseenter", () => this.handleHoverAction());

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        if (button.name === "") {
            element.style.visibility = "hidden";
        }

        element.addEventListener("click", () => void this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the hover on an action button
     *
     * @author Kyrylo Maliuha
     */
    private handleHoverAction(): void {
        this.playActionSound("/assets/sound/hover.mp3");
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        if (this._isTransitioning) {
            return;
        }

        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.render(true);

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        this.playActionSound(button.sound as string);

        if (button.alias === "start-puzzle") {
            this.handleStartPuzzleAction(button);
        }
        else {
            await this.transitionToState(state);
        }
    }

    /**
     * Handle the click on a start puzzle action button. Passes room alias to the puzzle component and shows it on the screen
     *
     * @param _button Action button that was clicked
     * @private
     *
     * @author Kyrylo Maliuha
     */
    private handleStartPuzzleAction(_button: ActionReference): void {
        if (this._isTransitioning || !this._currentGameState) {
            return;
        }

        const puzzleContainer: PuzzleComponent | null | undefined = this.shadowRoot?.querySelector("puzzle-container");

        if (puzzleContainer) {
            puzzleContainer.room = this._currentGameState.roomAlias;
            puzzleContainer.addEventListener("puzzle-complete", () => void this.handlePuzzleComplete());
        }
    }

    /**
     * Handle the completion of a puzzle
     *
     * @private
     *
     * @author Kyrylo Maliuha
     */
    private async handlePuzzleComplete(): Promise<void> {
        const state: GameState | undefined = await this._gameRouteService.executeAction("complete-puzzle");

        if (state) {
            await this.transitionToState(state);
        }
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render(true);

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        await this.transitionToState(state);
    }

    /**
     * Remove transition overlay from the screen
     *
     * @author Kyrylo Maliuha
     * @private
     */
    private fadeIn(): void {
        requestAnimationFrame(() => {
            const overlay: HTMLElement | null | undefined = this.shadowRoot?.getElementById("transitionOverlay");

            if (overlay) {
                overlay.classList.remove("active");
            }

            this._isTransitioning = false;
        });
    }

    /**
     * Start transition and show overlay on the screen
     *
     * @author Kyrylo Maliuha
     * @private
     */
    private fadeOut(): Promise<void> {
        return new Promise(resolve => {
            const overlay: HTMLElement | null | undefined = this.shadowRoot?.getElementById("transitionOverlay");

            if (overlay) {
                this._isTransitioning = true;
                overlay.classList.add("active");

                // this delay doesn't need to be changed to change the duration of the transition.
                // change --change-room-fade-in-duration variable in default.css instead to change it.
                setTimeout(resolve, 200);
            }
            else {
                resolve();
            }
        });
    }

    /**
     * Wrapper for updateGameState with transition effect
     *
     * @param state Game state to transition to
     * @author Kyrylo Maliuha
     * @private
     */
    private async transitionToState(state: GameState): Promise<void> {
        await this.fadeOut();
        await this.updateGameState(state);
    }

    /**
     * Set audio player's src to current room's sound URL, loop it and start playing
     *
     * @author Kyrylo Maliuha
     * @private
     */
    private playRoomSound(): void {
        if (!this._currentGameState) {
            return;
        }

        const soundUrl: string = this._currentGameState.roomSound;

        // If already playing this sound, do nothing
        if (this._roomAudio && this._roomAudio.src.endsWith(soundUrl)) {
            return;
        }

        // Stop and reset previous audio if any
        if (this._roomAudio) {
            this._roomAudio.pause();
            this._roomAudio.src = "";
            this._roomAudio = undefined;
        }

        if (!soundUrl) {
            return;
        }

        this._roomAudio = new Audio(soundUrl);
        this._roomAudio.loop = true;
        this._roomAudio.play().catch(console.log);
    }

    /**
     * Set action's soundUrl or default sound as src of the action audio player and play it once
     *
     * @param soundUrl URL of the sound to play
     * @author Kyrylo Maliuha
     * @private
     */
    private playActionSound(soundUrl: string = "/assets/sound/click.mp3"): void {
        if (!soundUrl) {
            return;
        }

        this._actionAudio = new Audio(soundUrl);
        this._actionAudio.play().catch((error: unknown): void => {
            console.log(error);
        });
    }
}
